import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SkillsExpertise = () => {
  const [loading, setLoading] = useState(true);
  const [groupedSkills, setGroupedSkills] = useState([]);
  const [tools, setTools] = useState([]);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const res = await fetch('https://spaceportfolio-api.rajansudhan0.workers.dev/api/skills');
        const data = await res.json();
        
        let fetchedTools = [];
        let otherSkills = [];

        data.forEach(skill => {
          try {
            const parsedNames = JSON.parse(skill.name);
            if (Array.isArray(parsedNames)) {
              parsedNames.forEach(item => {
                const itemName = typeof item === 'string' ? item : item.name;
                if (skill.category === 'Tools') {
                  const itemIcon = typeof item === 'object' && item.icon ? item.icon : skill.icon;
                  fetchedTools.push({ name: itemName, icon: itemIcon });
                } else {
                  otherSkills.push({ name: itemName, category: skill.category, icon: skill.icon });
                }
              });
            } else {
              throw new Error("Not array");
            }
          } catch (e) {
            if (skill.category === 'Tools') {
              fetchedTools.push({ name: skill.name, icon: skill.icon });
            } else {
              otherSkills.push({ name: skill.name, category: skill.category, icon: skill.icon });
            }
          }
        });

        const grouped = otherSkills.reduce((acc, skill) => {
          const cat = skill.category || 'General';
          if (!acc[cat]) {
            acc[cat] = {
              title: cat,
              // Simple fallback map to assign icons automatically based on common words
              icon: cat.toLowerCase().includes('data') ? 'Database' : 
                    cat.toLowerCase().includes('remote') ? 'SatelliteDish' : 
                    cat.toLowerCase().includes('web') ? 'Code' : 
                    cat.toLowerCase().includes('science') ? 'FlaskConical' :
                    cat.toLowerCase().includes('communication') ? 'Mic' : 'Briefcase',
              skills: []
            };
          }
          acc[cat].skills.push({ name: skill.name });
          return acc;
        }, {});

        // Sort descending by count or alphabetical
        const groupedArray = Object.values(grouped).sort((a, b) => b.skills.length - a.skills.length);
        
        setTools(fetchedTools);
        setGroupedSkills(groupedArray);
      } catch (err) {
        console.error('Failed to load skills:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSkills();
  }, []);


  return (
    <section id="skills" className="py-16 lg:py-24 bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-text-primary mb-6">
            Skills & Expertise
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            I bridge the gap between science and technology—combining geospatial intelligence, data analysis, and effective communication to solve real-world challenges in space research, environmental monitoring, and STEM education.          </p>
        </div>

        {/* Skill Categories */}
        {loading ? (
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {[1, 2, 3].map(i => (
               <div key={i} className="h-64 bg-white/5 animate-pulse rounded-2xl border border-white/10"></div>
            ))}
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {groupedSkills.map((category, index) => (
            <div key={index} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:shadow-accent/5 transition-smooth">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mr-4">
                  <Icon 
                    name={category.icon} 
                    size={24} 
                    className="text-accent"
                  />
                </div>
                <h3 className="font-heading text-xl font-semibold text-text-primary">
                  {category.title}
                </h3>
              </div>

              <ul className="grid grid-cols-1 gap-3">
                {category.skills.map((skill, skillIndex) => (
                  <li key={skillIndex} className="flex items-center space-x-3 group">
                    <div className="w-1.5 h-1.5 bg-accent rounded-full shadow-[0_0_8px_rgba(14,165,233,0.4)] group-hover:scale-125 transition-smooth"></div>
                    <span className="text-text-secondary font-medium group-hover:text-text-primary transition-smooth">
                      {skill.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        )}

        {/* Tools & Technologies */}
        <div className="text-center">
          <h3 className="font-heading text-2xl font-semibold text-text-primary mb-8">
            Tools & Technologies
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {tools.map((tool, index) => (
              <div 
                key={index}
                className="flex items-center space-x-3 bg-white/5 border border-white/10 px-6 py-3 rounded-full hover:bg-accent/10 hover:text-accent transition-smooth"
              >
                <Icon 
                  name={tool.icon || 'Wrench'} 
                  size={20}
                />
                <span className="font-medium">{tool.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Problem-Solving Approach */}
        <div className="mt-16 bg-white/5 backdrop-blur-md rounded-2xl p-8 lg:p-12 border border-white/10">
          <div className="text-center">
            <Icon name="Lightbulb" size={48} className="text-accent mx-auto mb-6" />
            <h3 className="font-heading text-2xl font-semibold text-text-primary mb-4">
              Scientific Thinking Meets Real-World Impact
            </h3>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
              I approach every challenge like a research mission—identify the core problem, explore data-driven patterns, validate with scientific models, and deliver actionable insights. Whether it’s analyzing satellite data, developing web-based platforms, or leading STEM outreach, I combine logic and innovation to drive meaningful outcomes.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsExpertise;